import React, {
  forwardRef, useEffect, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useForm, useController } from 'react-hook-form';
import { BsCheck } from 'react-icons/bs';

import Loader from '../Loader';
import ModalStandalone from '../../lib/ModalStandalone';
import { loadOptionsCase } from '../../utils';
import confirmStyles from '../../lib/withModalConfirm/modal.module.scss';

import styles from './move-folder-to-case-modal.module.scss';

const DEFAULT_MODE = 'move';

function getClassNameStatus(s) {
  let className = 'pending';
  switch (s) {
    case 'Litige':
      className = 'dispute';
      break;
    case 'En attente':
      className = 'pending';
      break;
    case 'En cours':
      className = 'in-progress';
      break;
    case 'Archivés':
      className = 'archived';
      break;
    default:
      className = 'pending';
      break;
  }
  return className;
}

const MoveFolderToCaseModal = forwardRef(({
  folder,
  initOptsMove = [],
  onMove,
  onCopy,
  onMoved,
}, ref) => {
  const modalRoot = useMemo(() => document.getElementById('modal'), []);

  const [mode, setMode] = useState(DEFAULT_MODE);
  const [optsMove, setOptsMove] = useState(initOptsMove);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState(null);

  const [pendingConfirm, setPendingConfirm] = useState({
    action: null,
    message: null,
  });

  const { control, watch, reset } = useForm();

  const { field } = useController({
    name: 'search',
    control,
    defaultValue: '',
  });

  const watchSearch = watch('search');

  useEffect(() => {
    setOptsMove(initOptsMove);
  }, [initOptsMove]);

  useEffect(() => {
    let cancelled = false;
    const fetchCases = async () => {
      setIsLoading(true);
      const cases = await loadOptionsCase(watchSearch || '');
      if (cancelled) return;
      setOptsMove(cases);
      setIsLoading(false);
    };
    fetchCases();
    return () => { cancelled = true; };
  }, [watchSearch]);

  function closeModal() {
    if (ref?.current?.close) ref.current.close();
    setPendingConfirm({ action: null, message: null });
    setSelectedCaseId(null);
    setMode(DEFAULT_MODE);
    reset({ search: '' });
  }

  function openConfirm(action, message) {
    setPendingConfirm({ action, message });
  }

  function confirmAction(bool) {
    if (bool && pendingConfirm?.action) pendingConfirm.action();
    setPendingConfirm({ action: null, message: null });
  }

  const selectedCaseLabel = useMemo(() => (
    (optsMove || []).find((c) => String(c.value) === String(selectedCaseId))?.label || ''
  ), [optsMove, selectedCaseId]);

  async function doTransfer() {
    if (!folder?._id || !selectedCaseId) return;
    if (mode === 'copy') {
      if (!onCopy) return;
      const response = await onCopy(folder, selectedCaseId);
      if (response?.status === 200 && onMoved) onMoved();
      closeModal();
      return;
    }
    if (!onMove) return;
    const response = await onMove({ _id: folder._id, case: selectedCaseId });
    if (response?.status === 200 && onMoved) onMoved();
    closeModal();
  }

  function requestTransfer() {
    if (!selectedCaseId || !selectedCaseLabel) return;
    const folderName = folder?.name || '';
    const message = mode === 'copy'
      ? `Copier le dossier « ${folderName} » vers l'affaire ${selectedCaseLabel} ? Le dossier d'origine restera dans la GED libre.`
      : `Déplacer le dossier « ${folderName} » vers l'affaire ${selectedCaseLabel} ? Il ne sera plus visible dans la GED libre.`;
    openConfirm(doTransfer, message);
  }

  return (
    <>
      {(pendingConfirm.action && pendingConfirm.message && modalRoot) && createPortal(
        <>
          <div className={confirmStyles['overlay-modal']} />
          <div className={confirmStyles.modal}>
            <p>{pendingConfirm.message}</p>
            <div className={confirmStyles['container-btn']}>
              <button onClick={() => confirmAction(true)}>
                <span>Confirmer</span>
              </button>
              <button onClick={() => confirmAction(false)}>
                <span>Annuler</span>
              </button>
            </div>
          </div>
        </>,
        modalRoot,
      )}

      <ModalStandalone ref={ref} handleClose={closeModal}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <h1>Déplacer vers une affaire</h1>

          <div className={styles.fieldSearch}>
            <input
              type="search"
              placeholder="Rechercher une affaire..."
              name={field.name}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
            />
          </div>

          <div className={styles.list}>
            {isLoading ? (
              <div className={styles.loader}>
                <Loader size={22} />
              </div>
            ) : (
              <ul>
                {(optsMove || []).map((c) => {
                  const isSelected = String(c.value) === String(selectedCaseId);
                  return (
                    <li
                      key={`case-${c.value}`}
                      className={isSelected ? styles.selected : ''}
                      onClick={() => setSelectedCaseId(c.value)}
                    >
                      <div className={`${styles.status} ${styles[getClassNameStatus(c.status)]}`} />
                      <p>{c.label}</p>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className={styles.toggleRow}>
            <div className={styles.toggle}>
              <button
                type="button"
                className={mode === 'move' ? styles.active : ''}
                onClick={() => setMode('move')}
              >
                Déplacer
              </button>
              <button
                type="button"
                className={mode === 'copy' ? styles.active : ''}
                onClick={() => setMode('copy')}
              >
                Copier
              </button>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              disabled={!selectedCaseId}
              onClick={requestTransfer}
            >
              <span className="icon"><BsCheck size="24px" /></span>
              <span> Valider </span>
            </button>
          </div>
        </div>
      </ModalStandalone>
    </>
  );
});

export default MoveFolderToCaseModal;
